const ga = (...args: any[]) => {
  if (typeof (window as any).gtag === 'function') {
    (window as any).gtag(...args);
  }
};

const PLAN_PRICES: Record<string, Record<string, number>> = {
  solo:    { monthly: 3, annual: 24 },
  pro:     { monthly: 5, annual: 48 },
  'team-micro':    { monthly: 30, annual: 288 },
  'team-starter':  { monthly: 40, annual: 432 },
  'team-firm':     { monthly: 50, annual: 576 },
};

function planValue(plan: string, interval: string, tier?: string | null): number {
  const key = tier ? `team-${tier.toLowerCase()}` : plan;
  return PLAN_PRICES[key]?.[interval] ?? 0;
}

function planLabel(plan: string, tier?: string | null): string {
  if (tier) return `Team ${tier}`;
  return plan === 'solo' ? 'Solo Plan' : plan === 'pro' ? 'Pro Plan' : plan;
}

export function trackSignUp(professionID?: string) {
  ga('event', 'sign_up', {
    method: 'email',
    ...(professionID ? { profession: professionID } : {}),
  });
}

export function trackBeginCheckout(plan: string, interval: string, tier?: string | null) {
  const value = planValue(plan, interval, tier);
  ga('event', 'begin_checkout', {
    currency: 'USD',
    value,
    items: [{ item_id: tier ? `team_${tier.toLowerCase()}` : plan, item_name: planLabel(plan, tier), price: value, quantity: 1 }],
    billing_interval: interval,
  });
}

export function trackEmailVerified() {
  ga('event', 'email_verified');
}

/**
 * Statcounter's counter.js has no SPA awareness — it fires once per script
 * execution, reading document.location at that moment it runs. React
 * Router's client-side navigation never re-executes it, so only the very
 * first page of a session ever gets counted. Re-injecting a fresh copy of
 * the script tag on every route change forces a new beacon per page.
 *
 * This is an UNOFFICIAL workaround — Statcounter doesn't document a
 * virtual-pageview API the way GA4 does. Verify it's actually generating
 * extra hits in the Statcounter dashboard after deploying.
 *
 * Relies on window.sc_project/sc_invisible/sc_security already being set
 * by the static inline script in public/index.html (counter.js just reads
 * whatever's currently on window each time it runs).
 */
export function trackStatcounterPageview() {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById('statcounter-script');
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.id = 'statcounter-script';
  script.type = 'text/javascript';
  script.async = true;
  script.src = `https://www.statcounter.com/counter/counter.js?${Date.now()}`;
  document.body.appendChild(script);
}

export function trackPurchase(plan: string, interval: string, sessionId?: string | null, tier?: string | null) {
  const dedupeKey = `ga_purchase_fired_${sessionId ?? 'nosession'}`;
  if (sessionStorage.getItem(dedupeKey)) return;
  sessionStorage.setItem(dedupeKey, '1');

  const value = planValue(plan, interval, tier);
  ga('event', 'purchase', {
    currency: 'USD',
    value,
    transaction_id: sessionId ?? `sub_${Date.now()}`,
    items: [{ item_id: tier ? `team_${tier.toLowerCase()}` : plan, item_name: planLabel(plan, tier), price: value, quantity: 1 }],
    billing_interval: interval,
  });
}
